import React, { useRef, useCallback, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useSearchParams } from 'react-router-dom';
import * as htmlToImage from 'html-to-image';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider, Paywall, NotFoundPage } from '@platform/core';
import { OrgDataProvider, useOrgData } from './contexts/OrgDataContext';
import { LocaleProvider, useLocale } from './contexts/LocaleContext';
import { WiseBoardView } from './components/OrgChart/WiseBoardView';
import { OrgChart, OrgChartRef } from './components/OrgChart/OrgChart';
import { StatsPanel } from './components/OrgChart/StatsPanel';
import { Legend } from './components/OrgChart/Legend';
import { Controls } from './components/OrgChart/Controls';
import { ExecutiveModal } from './components/OrgChart/ExecutiveModal';
import LanguageSelector from './components/LanguageSelector';
import { EmployeesPage } from './pages/EmployeesPage';
import { LoginPage } from './pages/LoginPage';
import { AdminPage } from './pages/AdminPage';
import { FirmDashboard } from './pages/FirmDashboard';
import './App.css';

// Header component
const Header: React.FC<{
  onExportPng: () => void;
  onExportSvg: () => void;
  onPrint: () => void;
  zoom: number;
  setZoom: (value: number) => void;
  onZoomReset: () => void;
  onAutoFit: () => void;
  textSize: number;
  setTextSize: (value: number) => void;
  onTextSizeReset: () => void;
  divisionWidth: number;
  setDivisionWidth: (value: number) => void;
  onDivisionWidthReset: () => void;
  onOpenExecutiveModal: () => void;
  editMode: boolean;
  setEditMode: (value: boolean) => void;
  showFunctionDetails: boolean;
  setShowFunctionDetails: (value: boolean) => void;
  showFunctions: boolean;
  setShowFunctions: (value: boolean) => void;
  showSections: boolean;
  setShowSections: (value: boolean) => void;
  canEdit: boolean;
  viewMode: 'orgboard' | 'personnel';
  onToggleViewMode: () => void;
}> = ({ onExportPng, onExportSvg, onPrint, zoom, setZoom, onZoomReset, onAutoFit, textSize, setTextSize, onTextSizeReset, divisionWidth, setDivisionWidth, onDivisionWidthReset, onOpenExecutiveModal, editMode, setEditMode, showFunctionDetails, setShowFunctionDetails, showFunctions, setShowFunctions, showSections, setShowSections, canEdit, viewMode, onToggleViewMode }) => {
  const { isSaving, divisions, focusedDivision, setFocusedDivision, focusedDepartment, setFocusedDepartment } = useOrgData();
  const { user, organization, isAdmin, isOrgAdmin, isFirmLead, isConsultant, firm, logout } = useAuth();
  const { t } = useLocale();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [layoutPopoverOpen, setLayoutPopoverOpen] = useState(false);

  // Get sorted divisions for the dropdown
  const sortedDivisions = Object.entries(divisions)
    .sort((a, b) => a[1].number - b[1].number);

  const handleDivisionFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFocusedDivision(value === 'all' ? null : value);
  };

  const handleDepartmentFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFocusedDepartment(value === 'all' ? null : parseInt(value, 10));
  };

  // Get departments for the focused division
  const focusedDivisionData = focusedDivision ? divisions[focusedDivision] : null;
  const departments = focusedDivisionData?.departments || [];

  return (
    <header className="header">
      {/* Mobile hamburger button */}
      <button
        className="hamburger-btn"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
        <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
        <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
      </button>

      {/* Logo - Brand identity (always visible) */}
      <div className="logo">
        <div>
          <div className="logo-text">
            {organization?.name || 'Organization'}
            {isSaving && <span className="saving-indicator"> ({t('general.loading')})</span>}
          </div>
          {organization?.logo ? (
            <img
              src={organization.logo}
              alt={organization.name}
              className="org-logo"
            />
          ) : organization?.emoji ? (
            <span className="org-emoji">{organization.emoji}</span>
          ) : null}
        </div>
      </div>

      {/* Division & Department Filters - same row on mobile */}
      <div className="header-division-filter">
        <select
          value={focusedDivision || 'all'}
          onChange={handleDivisionFilterChange}
          title={t('controls.filterByDivision')}
        >
          <option value="all">{t('controls.allDivisions')}</option>
          {sortedDivisions.map(([key, div]) => (
            <option key={key} value={key}>
              {div.icon} {div.number}. {div.name}
            </option>
          ))}
        </select>
        {/* Department filter - shown when division is selected */}
        {focusedDivision && departments.length > 0 && (
          <select
            className="mobile-department-filter"
            value={focusedDepartment !== null ? focusedDepartment.toString() : 'all'}
            onChange={handleDepartmentFilterChange}
            title={t('controls.filterByDepartment')}
          >
            <option value="all">{t('controls.allDepartments')}</option>
            {departments.map((dept, index) => (
              <option key={index} value={index.toString()}>
                {dept.number}. {dept.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Toggle Buttons - shared between mobile header and desktop menu */}
      <div className="header-toggles">
        {/* Company Setup Button - always first */}
        <button className="btn btn-company-setup" onClick={onOpenExecutiveModal} title={t('controls.companySetup')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
          <span className="toggle-label">{t('controls.companySetup')}</span>
        </button>

        {/* Edit Mode Toggle Button */}
        <button
          className={`btn btn-toggle-edit ${editMode ? 'active' : ''} ${!canEdit ? 'disabled' : ''}`}
          onClick={() => canEdit && setEditMode(!editMode)}
          disabled={!canEdit}
          title={canEdit ? t('controls.editMode') : t('controls.viewerNoEdit')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
          <span className="toggle-label">{t('controls.editMode')}</span>
        </button>

        {/* Show Sections Toggle Button */}
        <button
          className={`btn btn-toggle-sections ${showSections ? 'active' : ''}`}
          onClick={() => setShowSections(!showSections)}
          title={t('controls.showSections')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <line x1="3" y1="9" x2="21" y2="9" />
            <line x1="3" y1="15" x2="21" y2="15" />
          </svg>
          <span className="toggle-label">{t('controls.showSections')}</span>
        </button>

        {/* Show Functions Toggle Button */}
        <button
          className={`btn btn-toggle-functions ${showFunctions ? 'active' : ''}`}
          onClick={() => setShowFunctions(!showFunctions)}
          title={t('controls.showFunctions')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="8" y1="6" x2="21" y2="6" />
            <line x1="8" y1="12" x2="21" y2="12" />
            <line x1="8" y1="18" x2="21" y2="18" />
            <line x1="3" y1="6" x2="3.01" y2="6" />
            <line x1="3" y1="12" x2="3.01" y2="12" />
            <line x1="3" y1="18" x2="3.01" y2="18" />
          </svg>
          <span className="toggle-label">{t('controls.showFunctions')}</span>
        </button>

        {/* Function Details Toggle Button */}
        <button
          className={`btn btn-toggle-details ${showFunctionDetails ? 'active' : ''}`}
          onClick={() => setShowFunctionDetails(!showFunctionDetails)}
          title={t('controls.showDetails')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
          <span className="toggle-label">{t('controls.showDetails')}</span>
        </button>

        {/* Auto-Fit Zoom Button */}
        <button
          className="btn btn-autofit"
          onClick={onAutoFit}
          title={t('controls.fitToScreen')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 3H5a2 2 0 0 0-2 2v3" />
            <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
            <path d="M3 16v3a2 2 0 0 0 2 2h3" />
            <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
          </svg>
          <span className="toggle-label">{t('controls.fitToScreen')}</span>
        </button>

        {/* View Mode Toggle Button */}
        <button
          className={`btn btn-toggle-view ${viewMode === 'personnel' ? 'active' : ''}`}
          onClick={onToggleViewMode}
          title={viewMode === 'orgboard' ? t('controls.switchToPersonnel') : t('controls.switchToOrgBoard')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="7" r="4" />
            <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
          </svg>
          <span className="toggle-label">{viewMode === 'orgboard' ? t('controls.personnel') : t('controls.orgBoard')}</span>
        </button>

        {/* Layout Settings Button */}
        <div className="layout-popover-container">
          <button
            className={`btn btn-layout ${layoutPopoverOpen ? 'active' : ''}`}
            onClick={() => setLayoutPopoverOpen(!layoutPopoverOpen)}
            title={t('controls.layout')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
            </svg>
            <span className="toggle-label">{t('controls.layout')}</span>
          </button>
          {layoutPopoverOpen && (
            <>
              <div className="layout-popover-overlay" onClick={() => setLayoutPopoverOpen(false)} />
              <div className="layout-popover">
                <div className="layout-popover-header">
                  <span>{t('controls.layout')}</span>
                  <button className="layout-popover-close" onClick={() => setLayoutPopoverOpen(false)}>×</button>
                </div>
                <div className="layout-popover-content">
                  {/* Zoom Control */}
                  <div className="layout-control">
                    <label>{t('controls.zoom')}: {Math.round(zoom * 100)}%</label>
                    <div className="layout-control-row">
                      <input
                        type="range"
                        min="0.25"
                        max="2"
                        step="0.05"
                        value={zoom}
                        onChange={(e) => setZoom(parseFloat(e.target.value))}
                      />
                      <button className="btn-reset" onClick={onZoomReset}>↺</button>
                    </div>
                  </div>
                  {/* Text Size Control */}
                  <div className="layout-control">
                    <label>{t('controls.textSize')}: {textSize}%</label>
                    <div className="layout-control-row">
                      <input
                        type="range"
                        min="50"
                        max="150"
                        step="5"
                        value={textSize}
                        onChange={(e) => setTextSize(parseInt(e.target.value))}
                      />
                      <button className="btn-reset" onClick={onTextSizeReset}>↺</button>
                    </div>
                  </div>
                  {/* Division Width Control */}
                  <div className="layout-control">
                    <label>{t('controls.divisionWidth')}: {divisionWidth}%</label>
                    <div className="layout-control-row">
                      <input
                        type="range"
                        min="50"
                        max="150"
                        step="5"
                        value={divisionWidth}
                        onChange={(e) => setDivisionWidth(parseInt(e.target.value))}
                      />
                      <button className="btn-reset" onClick={onDivisionWidthReset}>↺</button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile Controls Row - Department filter (only when division is selected) */}
      {focusedDivision && departments.length > 0 && (
        <div className="mobile-controls-row">
          <select
            className="mobile-department-filter"
            value={focusedDepartment !== null ? focusedDepartment.toString() : 'all'}
            onChange={handleDepartmentFilterChange}
            title={t('controls.filterByDepartment')}
          >
            <option value="all">{t('controls.allDepartments')}</option>
            {departments.map((dept, index) => (
              <option key={index} value={index.toString()}>
                {dept.number}. {dept.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Mobile menu overlay */}
      {mobileMenuOpen && <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)} />}

      {/* Menu content - collapsible on mobile */}
      <div className={`header-menu ${mobileMenuOpen ? 'open' : ''}`}>
        {/* Navigation Group - Page links (only for users with edit permissions) */}
        {canEdit && (
          <div className="header-group nav-group">
            <Link to={`/employees${window.location.search}`} className="btn nav-link" onClick={() => setMobileMenuOpen(false)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              {t('nav.employees')}
            </Link>

            {isOrgAdmin && (
              <Link to="/admin" className="btn nav-link admin-link" onClick={() => setMobileMenuOpen(false)}>
                {t('nav.admin')}
              </Link>
            )}

            {/* Firm Dashboard link for firm members and admins */}
            {(isFirmLead || isConsultant) && firm && (
              <Link to="/firm" className="btn nav-link firm-link" onClick={() => setMobileMenuOpen(false)}>
                🏢 {firm.branding?.firmName || firm.name}
              </Link>
            )}
            {isAdmin && (
              <Link to="/firm" className="btn nav-link firm-link" onClick={() => setMobileMenuOpen(false)}>
                🏢 Firms
              </Link>
            )}
          </div>
        )}

        <div className="header-separator mobile-hide" />

        {/* Controls Group - Layout & Export settings */}
        <div className="header-group controls-group">
          <Controls
                onExportPng={onExportPng}
                onExportSvg={onExportSvg}
                onPrint={onPrint}
                zoom={zoom}
                setZoom={setZoom}
                onZoomReset={onZoomReset}
                textSize={textSize}
                setTextSize={setTextSize}
                onTextSizeReset={onTextSizeReset}
                divisionWidth={divisionWidth}
                setDivisionWidth={setDivisionWidth}
                onDivisionWidthReset={onDivisionWidthReset}
                onOpenExecutiveModal={onOpenExecutiveModal}
                canEdit={canEdit}
              />
        </div>

      </div>

      {/* User Group - Language & Account (outside header-menu for grid positioning) */}
      <div className="header-group user-group">
        <img
          src="/turnkeyos-logo.svg"
          alt="TurnKeyOS.AI"
          className="platform-logo"
        />
        <LanguageSelector />
        <div className="user-menu">
          <span className="user-name">{user?.name}</span>
          <button onClick={logout} className="btn btn-logout">{t('nav.logout')}</button>
        </div>
      </div>
    </header>
  );
};

// Main content
const OrgChartContent: React.FC = () => {
  const { canEdit } = useAuth();
  const { viewMode, toggleViewMode } = useOrgData();
  const [searchParams, setSearchParams] = useSearchParams();
  const wiseBoardRef = useRef<HTMLDivElement>(null);
  const orgChartRef = useRef<OrgChartRef>(null);
  const [zoom, setZoom] = useState(1);
  const [textSize, setTextSize] = useState(150); // Default text size 150%
  const [divisionWidth, setDivisionWidth] = useState(100);
  const [isExecutiveModalOpen, setIsExecutiveModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(canEdit); // Edit mode on only if user can edit
  const [showFunctionDetails, setShowFunctionDetails] = useState(true); // Show function details by default
  const [showFunctions, setShowFunctions] = useState(true); // Show functions by default
  const [showSections, setShowSections] = useState(true); // Show sections by default

  // Auto-open Executive Settings modal if openSettings=true query param is present
  useEffect(() => {
    if (searchParams.get('openSettings') === 'true') {
      setIsExecutiveModalOpen(true);
      // Remove the query param to prevent re-opening on refresh
      searchParams.delete('openSettings');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleZoomReset = useCallback(() => {
    setZoom(1);
  }, []);

  const handleTextSizeReset = useCallback(() => {
    setTextSize(150); // Reset to default 150%
  }, []);

  const handleDivisionWidthReset = useCallback(() => {
    setDivisionWidth(100);
  }, []);

  const handleAutoFit = useCallback(() => {
    if (!wiseBoardRef.current) return;

    // Scroll to Division 1 (left side for both LTR and RTL)
    const scrollToStart = () => {
      setTimeout(() => {
        const mainContent = document.querySelector('.main-content') as HTMLElement;
        if (mainContent) {
          const isRTL = document.documentElement.dir === 'rtl';
          if (isRTL) {
            // In RTL, scroll to left edge (negative scrollLeft in some browsers)
            // Use a large negative number to ensure we reach the left edge
            mainContent.scrollLeft = -(mainContent.scrollWidth);
          } else {
            mainContent.scrollLeft = 0;
          }
          mainContent.scrollTop = 0;
        }
      }, 100);
    };

    // If zoom is not 100%, toggle back to 100%
    if (Math.abs(zoom - 1) > 0.01) {
      setZoom(1);
      scrollToStart();
      return;
    }

    // Otherwise, calculate auto-fit zoom
    const element = wiseBoardRef.current;
    const boardWidth = element.scrollWidth;
    const boardHeight = element.scrollHeight;

    // Get viewport dimensions (accounting for header)
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight - 120; // Subtract header height

    // Calculate zoom to fit width and height
    const zoomToFitWidth = viewportWidth / boardWidth;
    const zoomToFitHeight = viewportHeight / boardHeight;

    // Use the smaller zoom to fit both dimensions
    const newZoom = Math.min(zoomToFitWidth, zoomToFitHeight, 1); // Cap at 100%
    const finalZoom = Math.max(newZoom, 0.1); // Minimum 10%

    setZoom(Math.round(finalZoom * 100) / 100); // Round to 2 decimal places
    scrollToStart();
  }, [zoom]);

  const handleExportPng = useCallback(async () => {
    const timestamp = Date.now();

    if (viewMode === 'personnel') {
      if (!orgChartRef.current) return;
      try {
        const base64 = await orgChartRef.current.exportPng();
        const link = document.createElement('a');
        link.download = `orgchart_${timestamp}.png`;
        link.href = base64;
        link.click();
      } catch (error) {
        console.error('PNG Export failed:', error);
      }
      return;
    }

    if (!wiseBoardRef.current) return;

    // Temporarily disable edit mode for clean export
    const wasEditMode = editMode;
    if (wasEditMode) {
      setEditMode(false);
      await new Promise(resolve => setTimeout(resolve, 100)); // Wait for re-render
    }

    try {
      console.log('PNG Export: Converting Org Board to PNG...');

      const element = wiseBoardRef.current;
      const fullWidth = element.scrollWidth;
      const fullHeight = element.scrollHeight;

      const pngDataUrl = await htmlToImage.toPng(element, {
        quality: 1,
        backgroundColor: '#f8fafc',
        width: fullWidth,
        height: fullHeight,
        style: {
          overflow: 'visible',
        },
      });

      const link = document.createElement('a');
      link.download = `orgboard_${timestamp}.png`;
      link.href = pngDataUrl;
      link.click();

      console.log('PNG Export: Done!');
    } catch (error) {
      console.error('PNG Export failed:', error);
    } finally {
      // Restore edit mode if it was on
      if (wasEditMode) {
        setEditMode(true);
      }
    }
  }, [editMode, viewMode]);

  const handleExportSvg = useCallback(async () => {
    const timestamp = Date.now();

    if (viewMode === 'personnel') {
      if (!orgChartRef.current) return;
      try {
        const svgString = await orgChartRef.current.exportSvg();
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `orgchart_${timestamp}.svg`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('SVG Export failed:', error);
      }
      return;
    }

    if (!wiseBoardRef.current) return;

    // Temporarily disable edit mode for clean export
    const wasEditMode = editMode;
    if (wasEditMode) {
      setEditMode(false);
      await new Promise(resolve => setTimeout(resolve, 100)); // Wait for re-render
    }

    try {
      console.log('SVG Export: Converting Org Board to SVG...');

      const element = wiseBoardRef.current;
      const fullWidth = element.scrollWidth;
      const fullHeight = element.scrollHeight;
      console.log(`SVG Export: Capturing full board ${fullWidth}x${fullHeight}`);

      const svgDataUrl = await htmlToImage.toSvg(element, {
        quality: 1,
        backgroundColor: '#f8fafc',
        width: fullWidth,
        height: fullHeight,
        style: {
          overflow: 'visible',
        },
      });

      const response = await fetch(svgDataUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `orgboard_${timestamp}.svg`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);

      console.log('SVG Export: Done!');
    } catch (error) {
      console.error('SVG Export failed:', error);
    } finally {
      // Restore edit mode if it was on
      if (wasEditMode) {
        setEditMode(true);
      }
    }
  }, [editMode, viewMode]);

  const handlePrint = useCallback(async () => {
    // Temporarily disable edit mode for clean print
    const wasEditMode = editMode;
    if (wasEditMode) {
      setEditMode(false);
      await new Promise(resolve => setTimeout(resolve, 100)); // Wait for re-render
    }

    window.print();

    // Restore edit mode if it was on
    if (wasEditMode) {
      setEditMode(true);
    }
  }, [editMode]);

  return (
    <div className="app-container">
      <Header
        onExportPng={handleExportPng}
        onExportSvg={handleExportSvg}
        onPrint={handlePrint}
        zoom={zoom}
        setZoom={setZoom}
        onZoomReset={handleZoomReset}
        onAutoFit={handleAutoFit}
        textSize={textSize}
        setTextSize={setTextSize}
        onTextSizeReset={handleTextSizeReset}
        divisionWidth={divisionWidth}
        setDivisionWidth={setDivisionWidth}
        onDivisionWidthReset={handleDivisionWidthReset}
        onOpenExecutiveModal={() => setIsExecutiveModalOpen(true)}
        editMode={editMode}
        setEditMode={setEditMode}
        showFunctionDetails={showFunctionDetails}
        setShowFunctionDetails={setShowFunctionDetails}
        showFunctions={showFunctions}
        setShowFunctions={setShowFunctions}
        showSections={showSections}
        setShowSections={setShowSections}
        canEdit={canEdit}
        viewMode={viewMode}
        onToggleViewMode={toggleViewMode}
      />

      <main className="main-content">
        {viewMode === 'personnel' ? (
          <div className="personnel-layout">
            <div className="personnel-sidebar">
              <StatsPanel />
              <Legend />
            </div>
            <div className="personnel-chart">
              <OrgChart ref={orgChartRef} />
            </div>
          </div>
        ) : (
          <div className="zoom-container" style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
            <WiseBoardView ref={wiseBoardRef} textSize={textSize} divisionWidth={divisionWidth} zoom={zoom} editMode={editMode} showFunctionDetails={showFunctionDetails} showFunctions={showFunctions} showSections={showSections} />
          </div>
        )}
      </main>

      <ExecutiveModal
        isOpen={isExecutiveModalOpen}
        onClose={() => setIsExecutiveModalOpen(false)}
      />
    </div>
  );
};

// Main app content (protected)
const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, organization, isAdmin, isFirmLead, isConsultant, firm, selectOrganization } = useAuth();
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const [orgLoading, setOrgLoading] = useState(false);

  // Handle org query parameter for admin/firm users
  useEffect(() => {
    const orgId = searchParams.get('org');
    if (orgId && (isAdmin || isFirmLead || isConsultant) && !isLoading && (!organization || organization.id !== orgId)) {
      setOrgLoading(true);
      selectOrganization(orgId).finally(() => setOrgLoading(false));
    }
  }, [searchParams, isAdmin, isFirmLead, isConsultant, isLoading, organization, selectOrganization]);

  if (isLoading || orgLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>{t('general.loading')}</p>
      </div>
    );
  }

  // Not authenticated - show login
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Firm lead or consultant without org selected - redirect to firm dashboard
  if (!organization && (isFirmLead || isConsultant) && firm) {
    return (
      <Routes>
        <Route path="/firm" element={<FirmDashboard />} />
        <Route path="*" element={<Navigate to="/firm" replace />} />
      </Routes>
    );
  }

  // Authenticated but no organization selected (admin needs to select one)
  if (!organization && !isAdmin && !isFirmLead && !isConsultant) {
    return (
      <div className="no-org-screen">
        <h2>{t('msg.noOrganization')}</h2>
        <p>{t('msg.noOrganizationDesc')}</p>
      </div>
    );
  }

  // Authenticated - show main app
  return (
    <OrgDataProvider>
      <Routes>
        <Route path="/" element={<OrgChartContent />} />
        <Route path="/employees" element={<EmployeesPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/firm" element={<FirmDashboard />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </OrgDataProvider>
  );
};

// Wrap content with subscription paywall
function AppContentWithPaywall() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Firm leads: subscription is at firm level, not org level
  const subscriptionId = user?.firmId || user?.organizationId || undefined;

  return (
    <SubscriptionProvider organizationId={subscriptionId}>
      <Paywall isAdmin={isAdmin}>
        <AppContent />
      </Paywall>
    </SubscriptionProvider>
  );
}

// App with AuthProvider and LocaleProvider
function App() {
  return (
    <LocaleProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <AppContentWithPaywall />
        </AuthProvider>
      </BrowserRouter>
    </LocaleProvider>
  );
}

export default App;
